import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pin, PinOff } from 'lucide-react';
import api from '../services/api';
import HubTileArt, { getTileInnerStyle } from '../components/HubTileArt';
import LimitedTopBar from './LimitedTopBar';
import { useLimitedShell } from './LimitedShellContext';
import { isExternalUrl } from './limitedPages';
import {
  GLASS_CARD_STYLE,
  ENCON_ORANGE,
  ENCON_BLACK,
  ENCON_GREY,
  BODY_TEXT,
  MUTED_TEXT,
  QUICK_LINK_MIME,
} from './limitedStyles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const greetingFor = (date) => {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const firstNameOf = (user) => {
  const full = (user?.display_name || user?.full_name || '').trim();
  if (full) return full.split(/\s+/)[0];
  const local = (user?.email || user?.username || '').split('@')[0];
  const part = local.split(/[._-]/)[0];
  return part ? part.charAt(0).toUpperCase() + part.slice(1) : '';
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const seconds = Math.max(0, Math.round((Date.now() - then.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return then.toLocaleDateString('en-GB');
};

const CardTitle = ({ children }) => (
  <h2 className="text-base font-bold mb-3" style={{ color: ENCON_ORANGE }}>{children}</h2>
);

const LimitedHome = ({ user }) => {
  const navigate = useNavigate();
  const { quickLinks, linkGroups, pinnedIds, pinLink, unpinLink } = useLimitedShell();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  // Recent activity: what this user has been doing. Refreshes every minute.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get(`${API}/limited/activity?limit=25`);
        if (cancelled) return;
        setEvents(Array.isArray(response.data?.events) ? response.data.events : []);
        setLoadFailed(false);
      } catch (error) {
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  const now = new Date();
  const dateLabel = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const name = firstNameOf(user);

  const linksForGroup = (groupId) => quickLinks
    .filter((link) => (link.group_id || 'default') === groupId)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const visibleGroups = linkGroups.filter((group) => linksForGroup(group.id).length > 0);

  const openLink = (link) => {
    if (isExternalUrl(link.url)) window.open(link.url, '_blank', 'noopener,noreferrer');
    else navigate(link.url);
  };

  return (
    <div data-testid="limited-home" className="min-h-screen">
      <LimitedTopBar
        user={user}
        left={(
          <div
            className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0"
            style={{ background: 'rgba(0,0,0,0.22)' }}
          >
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Home</span>
          </div>
        )}
      />

      <div className="px-4 lg:px-8 pt-8 pb-10">
        <div className="max-w-[1200px] mx-auto">
          {/* Greeting */}
          <div className="text-center mb-8">
            <p className="text-sm font-semibold" style={{ color: MUTED_TEXT }}>{dateLabel}</p>
            <h1 className="text-3xl md:text-4xl font-bold mt-1" style={{ color: ENCON_BLACK }}>
              {greetingFor(now)}{name ? ', ' : ''}
              {name && <span style={{ color: ENCON_ORANGE }}>{name}</span>}
            </h1>
            <p className="text-xs font-bold mt-2" style={{ color: ENCON_GREY }}>The Distributor of Choice</p>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Recent activity - the main window */}
            <section
              data-testid="limited-activity-card"
              className="lg:col-span-2 rounded-3xl p-5"
              style={GLASS_CARD_STYLE}
            >
              <CardTitle>Recent activity</CardTitle>
              {loading ? (
                <p className="text-sm" style={{ color: MUTED_TEXT }}>Loading...</p>
              ) : loadFailed ? (
                <p className="text-sm" style={{ color: MUTED_TEXT }}>Recent activity could not be loaded. It will retry shortly.</p>
              ) : events.length === 0 ? (
                <p className="text-sm" style={{ color: MUTED_TEXT }} data-testid="limited-activity-empty">
                  Nothing yet. Transfers you create or edit will show up here.
                </p>
              ) : (
                <ul className="space-y-2">
                  {events.map((event) => (
                    <li key={event.id}>
                      <button
                        type="button"
                        onClick={() => navigate(event.route)}
                        data-testid={`limited-activity-${event.id}`}
                        className="w-full flex items-center gap-3 text-left rounded-2xl px-2 py-2 hover:bg-white/70 transition-colors"
                      >
                        <span
                          className="relative block w-11 h-11 rounded-xl overflow-hidden shrink-0"
                          style={getTileInnerStyle(event.page, 'light')}
                        >
                          <HubTileArt artKey={event.page} theme="light" className="absolute inset-0 w-full h-full" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-bold truncate" style={{ color: BODY_TEXT }}>{event.title}</span>
                          <span className="block text-xs truncate" style={{ color: MUTED_TEXT }}>{event.detail}</span>
                        </span>
                        <span className="text-xs font-semibold shrink-0" style={{ color: MUTED_TEXT }}>
                          {timeAgo(event.timestamp)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Quick links - the burger menu links; drag one into the sidebar (or tap the pin) */}
            <section
              data-testid="limited-quick-links-card"
              className="rounded-3xl p-5"
              style={GLASS_CARD_STYLE}
            >
              <CardTitle>Quick links</CardTitle>
              <p className="text-xs mb-3" style={{ color: MUTED_TEXT }}>
                Drag a link into the sidebar, or tap the pin, to keep it there.
              </p>
              {visibleGroups.length === 0 ? (
                <p className="text-sm" style={{ color: MUTED_TEXT }}>No quick links available.</p>
              ) : (
                visibleGroups.map((group, groupIndex) => (
                  <div key={group.id} className={groupIndex > 0 ? 'mt-3' : ''}>
                    <p
                      className="text-[10px] font-bold uppercase tracking-wider mb-1"
                      style={{ color: group.color || '#6d28d9' }}
                    >
                      {group.name}
                    </p>
                    <div className="space-y-0.5">
                      {linksForGroup(group.id).map((link) => {
                        const pinned = pinnedIds.includes(link.id);
                        return (
                          <div
                            key={link.id}
                            draggable
                            onDragStart={(event) => {
                              event.dataTransfer.setData(QUICK_LINK_MIME, link.id);
                              event.dataTransfer.setData('text/plain', link.name);
                              event.dataTransfer.effectAllowed = 'move';
                            }}
                            data-testid={`limited-quick-link-${link.id}`}
                            className="flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-white/70 cursor-grab active:cursor-grabbing"
                          >
                            <button
                              type="button"
                              onClick={() => openLink(link)}
                              className="flex-1 min-w-0 text-left text-sm font-medium truncate"
                              style={{ color: group.color || '#6d28d9' }}
                            >
                              {link.name}
                            </button>
                            <button
                              type="button"
                              onClick={() => (pinned ? unpinLink(link.id) : pinLink(link.id))}
                              title={pinned ? 'Remove from sidebar' : 'Pin to sidebar'}
                              data-testid={`limited-quick-link-pin-${link.id}`}
                              className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-orange-50"
                            >
                              {pinned
                                ? <PinOff className="w-4 h-4" style={{ color: ENCON_ORANGE }} />
                                : <Pin className="w-4 h-4" style={{ color: ENCON_GREY }} />}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LimitedHome;
